(() => {
  const cents = (value) => Number.isSafeInteger(value) && value >= 0;
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

  function basisPointsForValue(value) {
    if (!cents(value)) throw new TypeError('Invalid merchandise value');
    return value <= 16000 ? 200 : value <= 32000 ? 250 : 300;
  }

  function feeForValue(value) {
    if (!cents(value)) throw new TypeError('Invalid item value');
    if (value === 0) return 0;
    if (value <= 10000) return 95;
    return Math.round(value * basisPointsForValue(value) / 10000);
  }

  function previewQuote(input, choices = {}) {
    if (input.currency !== 'USD') return { currency: input.currency, unavailable: true, shipments: [] };
    const items = input.items || [];
    if (items.some((item) => !cents(item.value))) throw new TypeError('Invalid cart value');
    const total = items.reduce((sum, item) => sum + item.value, 0);
    const discount = Math.min(total, Math.max(0, Math.round(input.cartDiscount || 0)));
    const allocations = items.map((item, index) => {
      const exact = total ? discount * item.value / total : 0;
      return { index, amount: Math.floor(exact), remainder: exact % 1 };
    });
    let remaining = discount - allocations.reduce((sum, item) => sum + item.amount, 0);
    [...allocations].sort((a, b) => b.remainder - a.remainder || a.index - b.index).forEach((item) => {
      if (remaining > 0) { allocations[item.index].amount += 1; remaining -= 1; }
    });
    const groups = new Map();
    items.forEach((item, index) => {
      if (!item.eligible || item.delivery === 'pickup') return;
      const id = item.shipmentId || 'single';
      if (!groups.has(id)) groups.set(id, { id, label: item.label || input.strings?.shipment || 'This shipment', subtotal: 0 });
      groups.get(id).subtotal += item.value - allocations[index].amount;
    });
    const shipments = [...groups.values()].filter((item) => item.subtotal > 0).map((item) => ({
      ...item,
      fee: feeForValue(item.subtotal),
      selected: own(choices, item.id) ? choices[item.id] === true : input.previewDefault === true,
    }));
    return { currency: 'USD', preview: true, legacy: items.some((item) => item.legacy), shipments };
  }

  function validateQuote(quote) {
    if (!quote || quote.currency !== 'USD' || !quote.quoteId || !quote.revision || !Array.isArray(quote.shipments)) throw new TypeError('Invalid protection quote');
    const ids = new Set();
    for (const item of quote.shipments) {
      if (typeof item.id !== 'string' || !item.id || ids.has(item.id) || typeof item.label !== 'string' || !cents(item.subtotal) || !cents(item.fee) || typeof item.selected !== 'boolean') throw new TypeError('Invalid shipment');
      ids.add(item.id);
    }
    return quote;
  }

  const api = { feeForValue, previewQuote, validateQuote };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }
  if (window.EdelweissProtection) return;

  const money = (value, currency = 'USD') => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(value / 100);
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const components = new Set();
  const sessions = new Map();
  let config;
  let quote;
  let adapter;
  let busy = false;
  let failed = false;
  let request = 0;
  let refreshTimer;

  function choices() {
    if (!config) return {};
    const key = `ew-protection-preview-v1:${config.identity}`;
    if (!sessions.has(key)) {
      try { sessions.set(key, JSON.parse(sessionStorage.getItem(key) || '{}')); }
      catch (_) { sessions.set(key, {}); }
    }
    const value = sessions.get(key);
    if (!value || typeof value !== 'object' || Array.isArray(value)) sessions.set(key, {});
    return sessions.get(key);
  }

  function saveChoice(id, selected) {
    const current = choices();
    current[id] = selected;
    try { sessionStorage.setItem(`ew-protection-preview-v1:${config.identity}`, JSON.stringify(current)); }
    catch (_) {}
  }

  function render(error = '') {
    components.forEach((component) => component.render(quote, busy, error));
  }

  function snapshot(cart) {
    return {
      ...config,
      currency: cart.currency,
      cartDiscount: (cart.cart_level_discount_applications || []).reduce((sum, discount) => sum + discount.total_allocated_amount, 0),
      items: (cart.items || []).map((item) => {
        const handle = (item.handle || '').toLowerCase();
        const legacy = /shipping-insurance|shipping-protection/.test(handle) || handle === 'order-protection' || item.properties?._edelweiss_shipping_protection === 'true';
        return { value: item.final_line_price, eligible: !item.gift_card && item.requires_shipping !== false && !legacy && !handle.includes('warm-weather'), legacy };
      }),
    };
  }

  async function refresh(cart) {
    if (!config || components.size === 0) return;
    if (config.mode === 'connected' && !adapter) { busy = true; render(); return; }
    const serial = ++request;
    if (config.mode === 'connected') { busy = true; render(); }
    try {
      if (!cart?.items || !cart.currency) {
        const response = await fetch(`${config.root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error('Cart unavailable');
        cart = await response.json();
      }
      if (serial !== request) return;
      config = snapshot(cart);
      if (config.mode === 'preview') {
        quote = previewQuote(config, choices());
      } else {
        busy = true;
        render();
        if (!adapter) throw new Error('Protection connection unavailable');
        const next = validateQuote(await adapter.getQuote({ cart }));
        if (serial !== request) return;
        quote = next;
      }
      failed = false;
    } catch (error) {
      console.error('Edelweiss protection:', error.message);
      if (serial !== request) return;
      failed = true;
      render(config.strings.unavailable);
    } finally {
      if (serial === request) { busy = false; render(failed ? config.strings.unavailable : ''); }
    }
  }

  function queueRefresh(event) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refresh(event?.cartData || event?.detail?.cart), 80);
  }

  async function select(id, selected) {
    if (busy || !quote?.shipments.some((item) => item.id === id)) return;
    if (config.mode === 'preview') {
      saveChoice(id, selected);
      quote = previewQuote(config, choices());
      render();
      return;
    }
    const serial = ++request;
    busy = true;
    render();
    try {
      if (!adapter) throw new Error('No adapter');
      const next = validateQuote(await adapter.setSelection({ quoteId: quote.quoteId, revision: quote.revision, shipmentId: id, selected }));
      if (serial !== request) return;
      const applied = next.shipments.find((item) => item.id === id);
      if (!applied || applied.selected !== selected) throw new Error('Selection not confirmed');
      quote = next;
      failed = false;
    } catch (error) {
      console.error('Edelweiss protection selection:', error.message);
      if (serial === request) { failed = true; render(config.strings.error); }
    } finally {
      if (serial === request) { busy = false; render(failed ? config.strings.error : ''); }
    }
  }

  class Protection extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-protection-config]').textContent);
        config = this.config;
        components.add(this);
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-protection-shipment]');
          if (!button) return;
          event.preventDefault();
          select(button.dataset.protectionShipment, button.getAttribute('aria-checked') !== 'true');
        });
        if (config.mode === 'preview') { quote = previewQuote(config, choices()); render(); }
        else { this.render(null, true); queueRefresh(); }
      } catch (_) { this.querySelector('[data-protection-notice]').hidden = false; }
    }
    disconnectedCallback() { components.delete(this); }
    render(current, pending, error = '') {
      const strings = this.config.strings;
      const count = current?.shipments.length || 0;
      this.dataset.single = String(count === 1);
      const rows = this.querySelector('[data-protection-shipments]');
      const focused = this.contains(document.activeElement) ? document.activeElement.dataset.protectionShipment : null;
      const fragment = document.createDocumentFragment();
      (current?.shipments || []).forEach((item) => {
        const row = element('div', 'ew-protection__shipment');
        const copy = element('div');
        copy.append(element('span', 'ew-protection__shipment-label', item.label));
        copy.append(element('span', 'ew-protection__shipment-value', `${strings.merchandise}: ${money(item.subtotal)}`));
        const price = element('strong', 'ew-protection__price', money(item.fee));
        const button = element('button', 'ew-protection__switch');
        button.type = 'button';
        button.setAttribute('role', 'switch');
        button.setAttribute('aria-checked', String(item.selected));
        button.setAttribute('aria-label', `${strings.toggle} ${item.label}: ${money(item.fee)}`);
        button.dataset.protectionShipment = item.id;
        button.disabled = pending;
        row.append(copy, price, button);
        fragment.append(row);
      });
      const selectedCount = current?.shipments.filter((item) => item.selected).length || 0;
      if (count > 1) {
        const total = current.shipments.reduce((sum, item) => sum + (item.selected ? item.fee : 0), 0);
        const totalRow = element('div', 'ew-protection__total');
        totalRow.append(element('span', '', strings.total), element('span', '', money(total)));
        fragment.append(totalRow);
      }
      rows.replaceChildren(fragment);
      const calculations = document.createDocumentFragment();
      (current?.shipments || []).forEach((item) => {
        const calculation = element('div', 'ew-protection__calculation');
        if (count > 1) calculation.append(element('span', 'ew-protection__calculation-label', item.label));
        const equation = element('div', 'ew-protection__equation');
        if (item.subtotal > 0 && item.subtotal <= 10000 && item.fee === 95) {
          equation.append(element('span', '', strings.flat_fee), element('strong', '', money(item.fee)));
        } else if (item.fee === feeForValue(item.subtotal)) {
          equation.append(element('span', '', `${money(item.subtotal)} × ${basisPointsForValue(item.subtotal) / 100}%`), element('strong', '', `= ${money(item.fee)}`));
        } else {
          calculation.append(element('span', 'ew-protection__calculation-label', `${strings.merchandise}: ${money(item.subtotal)}`));
          equation.append(element('span', '', strings.protection_fee), element('strong', '', money(item.fee)));
        }
        calculation.append(equation);
        calculations.append(calculation);
      });
      this.querySelector('[data-protection-basis]').replaceChildren(calculations);
      this.querySelector('[data-protection-quote]').hidden = !count;
      if (focused) [...rows.querySelectorAll('[data-protection-shipment]')].find((button) => button.dataset.protectionShipment === focused)?.focus({ preventScroll: true });
      const state = this.querySelector('[data-protection-state]');
      state.textContent = pending ? strings.updating : current?.unavailable ? strings.currency : !count ? strings.empty : selectedCount ? (current.preview ? strings.selected_preview : strings.included) : strings.declined;
      const notice = this.querySelector('[data-protection-notice]');
      notice.hidden = this.config.mode !== 'preview';
      notice.textContent = current?.legacy ? `${strings.preview_notice} ${strings.legacy_notice}` : strings.preview_notice;
      const alert = this.querySelector('[data-protection-error]');
      alert.hidden = !error;
      alert.textContent = error;
      this.setAttribute('aria-busy', String(pending));
    }
  }

  class ProtectionExample extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('input', () => this.update());
      this.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-example-toggle]');
        if (toggle) { toggle.setAttribute('aria-checked', String(toggle.getAttribute('aria-checked') !== 'true')); this.update(); }
        if (event.target.closest('[data-example-reset]')) {
          this.querySelectorAll('input').forEach((input) => { input.value = input.defaultValue; });
          this.querySelectorAll('[data-example-toggle]').forEach((button) => button.setAttribute('aria-checked', 'true'));
          this.update();
        }
      });
      this.update();
    }
    update() {
      let total = 0;
      let valid = true;
      this.querySelectorAll('[data-example-row]').forEach((row) => {
        const input = row.querySelector('input');
        const value = Math.round(Number(input.value) * 100);
        const okay = input.value.trim() !== '' && input.checkValidity() && cents(value);
        input.setAttribute('aria-invalid', String(!okay));
        valid = valid && okay;
        const fee = okay ? feeForValue(value) : 0;
        row.querySelector('[data-example-fee]').textContent = okay ? money(fee) : '—';
        if (row.querySelector('[data-example-toggle]').getAttribute('aria-checked') === 'true') total += fee;
      });
      this.querySelector('[data-example-total]').textContent = valid ? money(total) : '—';
      this.querySelector('[data-example-error]').hidden = valid;
    }
  }

  api.failConnection = () => { busy = false; failed = true; if (config) render(config.strings.unavailable); };
  api.registerAdapter = (next) => {
    if (!next || typeof next.getQuote !== 'function' || typeof next.setSelection !== 'function') throw new TypeError('Protection adapter requires getQuote and setSelection');
    adapter = next;
    if (config?.mode === 'connected') queueRefresh();
  };
  window.EdelweissProtection = api;
  customElements.define('ew-shipping-protection', Protection);
  customElements.define('ew-protection-example', ProtectionExample);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => { if (event.source !== 'ew-shipping-protection') queueRefresh(event); });
  document.addEventListener('cart:updated', queueRefresh);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') queueRefresh(); });
  document.addEventListener('submit', (event) => {
    if (config?.mode !== 'connected' || (!busy && !failed)) return;
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    render(config.strings.unavailable);
  }, true);
})();

